import clsx from 'clsx';
import React, { HTMLAttributes, useRef, ReactNode, useState, ChangeEventHandler, useEffect, useCallback, useMemo } from 'react';

import { Icon } from 'components/Icon';
import { IconName } from 'components/Icon/IconName';
import { FieldError } from 'components/FieldError';
import { useCombinedRef } from 'hooks/useCombinedRef';
import { usePropSplit } from 'hooks/usePropSplit';
import { Input, InputProps } from '@clover/web-lib-consumer-ui';
import { CustomErrorMessage, ValidationError, ValidationEventHandler } from 'components/Input.type';

import { generateId } from 'util/generateId';
import { determineErrorMessage } from 'util/ErrorHandling/errorHandling.util';

import styles from 'components/TextField.module.scss';

// All the input props we want to hoist to the top-level of props instead of nesting in `inputProps`
const HOISTED_PROPS = [
  'disabled',
  'max',
  'min',
  'name',
  'type',
  'onChange',
  'onFocus',
  'pattern',
  'placeholder',
  'readOnly',
  'required',
  'step',
  'value',
  'defaultValue',
  'onBlur',
  'customError',
  'validateField',
  'onInvalid',
  'onKeyPress',
  'onKeyDown',
  'onKeyUp',
] as const;

// All the props we don't want to allow. Usually because we want to override them or define them differently.
const OMITTED_PROPS = ['textfield'] as const;

// A string union type out of the above HOISTED_PROPS list
// See https://stackoverflow.com/questions/45251664/typescript-derive-union-type-from-tuple-array-values
type HoistedPropNames = typeof HOISTED_PROPS[number];
type OmittedPropNames = typeof OMITTED_PROPS[number];

type AllowedInputProps = Omit<InputProps, OmittedPropNames>
type HoistedProps = Pick<AllowedInputProps, HoistedPropNames>;

export type TextFieldProps = Omit<HTMLAttributes<HTMLDivElement>, HoistedPropNames | OmittedPropNames> & HoistedProps & {
  label?: ReactNode;
  labelClass?: string;
  inputProps?: AllowedInputProps & { 'data-testid'?: string };
  inputRef?: React.Ref<HTMLInputElement>;
  helpText?: ReactNode;
  hideErrorText?: boolean;
  // What error text to show when there is a validation error. If not provided the browsers validationMessage is used.
  errorMessage?: CustomErrorMessage;
  icon?: IconName | ReactNode;
  trailingIcon?: IconName | ReactNode;
}

// Holds the error info that comes from the HTML5 constraint api
type InputError = {
  errors: ValidationError[];
  validationMessage?: string;
}

export const TextField: React.FC<TextFieldProps> = (props) => {
  const { className, label, labelClass, helpText, inputProps, hideErrorText, errorMessage, icon, trailingIcon, inputRef, children, ...remainingProps } = props;
  const [passThroughContainerProps, hoistedInputProps] = usePropSplit<HoistedProps, typeof remainingProps>(remainingProps, HOISTED_PROPS);
  const passThroughInputProps = useMemo(() => ({ ...inputProps, ...hoistedInputProps }) as AllowedInputProps, [hoistedInputProps, inputProps]);
  const [inputHeight, setInputHeight] = useState<number>();

  const [error, setError] = useState<InputError>();

  const errorText = determineErrorMessage({
    errors: error?.errors || [],
    validationMessage: error?.validationMessage,
    errorMessage,
    customError: passThroughInputProps.customError,
  });

  const inputElmRef = useCombinedRef<HTMLInputElement>(inputRef);
  const generatedId = useRef(generateId()).current;
  const helperTextId = useRef(generateId()).current;
  const errorTextId = useRef(generateId()).current;

  // Store the height of the input so we can style the absolute positioned icons
  // The container height will include the error and helpText so we can't position things based on height 100% and
  // contain them inside the input
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!inputElmRef.current) return;
    if (!icon && !trailingIcon) return; // we don't care about the input height if there are no icons to position
    if (inputHeight !== inputElmRef.current.clientHeight) setInputHeight(inputElmRef.current.clientHeight);
  });

  const wrapperClassName = clsx(styles.inputWrapper, {
    [styles.withTrailingIcon]: trailingIcon,
    [styles.withLabel]: label,
  }, className);

  // Don't allow a placeholder if label is being used but always have a truthy placeholder so we
  // can use `:placeholder-shown` in css to determine if the input is empty
  const placeholder = passThroughInputProps.placeholder && !label ? passThroughInputProps.placeholder : ' ';

  const handleChange: ChangeEventHandler<HTMLElement> = useCallback((e) => {
    setError(undefined); // If the user interacts with the inputs then hide the errors for now

    if (passThroughInputProps.onChange) passThroughInputProps.onChange(e as React.ChangeEvent<HTMLInputElement>);
  }, [passThroughInputProps]);

  const handleValidityChange: ValidationEventHandler<HTMLElement> = useCallback((e, errors, validationMessage) => {
    if (!errors.length) {
      setError(undefined); // Pick any of the errors to use
    } else {
      setError({ errors, validationMessage }); // Just use the first error and ignore the rest for now
    }

    if (passThroughInputProps.onInvalid) passThroughInputProps.onInvalid(e, errors, validationMessage);
  }, [passThroughInputProps]);

  return (
    <div className={wrapperClassName} {...passThroughContainerProps}>
      {icon && (<i className={styles.iconLeading}>{typeof icon === 'string' ? <Icon name={icon} /> : icon}</i>)}
      <Input
        validateOnBlur={true}
        {...passThroughInputProps}
        placeholder={placeholder}
        id={passThroughInputProps.id || generatedId}
        ref={inputElmRef}
        aria-describedby={clsx(helpText && helperTextId, errorText && errorTextId, passThroughInputProps['aria-describedby'])}
        onInvalid={handleValidityChange}
        onChange={handleChange}
      />
      {trailingIcon && (<i className={styles.iconTrailing} style={{ height: inputHeight }}>{typeof trailingIcon === 'string' ? <Icon name={trailingIcon} /> : trailingIcon}</i>)}
      {label && <label htmlFor={passThroughInputProps.id || generatedId} className={labelClass}>{label}</label>}
      {!hideErrorText && !passThroughInputProps.disabled && !passThroughInputProps.readOnly && (
        <FieldError id={errorTextId} className={styles.errorText}>{errorText}</FieldError>
      )}
      {helpText && <span className={styles.helperText} id={helperTextId}>{helpText}</span>}
      {children}
    </div>
  );
};
